import { useState, useEffect } from "react"

// 1024px is the desktop floor for the Orca shell. Chrome's Laptop preset is
// 1024 × 768 and should render the full desktop workspace; narrower tablet and
// phone widths use the mobile single-column shell.
// Must stay in sync with the consolidated mobile @media block in orca-engine-theme.css (ADR-125).
const MOBILE_BREAKPOINT = 1024

/**
 * Reactive hook for mobile viewport detection and keyboard state.
 * Uses matchMedia for breakpoint (no resize listener needed).
 * Uses visualViewport API for iOS keyboard detection.
 */
export function useMobileViewport() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
  )
  const [keyboardHeight, setKeyboardHeight] = useState(0)

  // Breakpoint detection via matchMedia
  useEffect(() => {
    if (typeof window === "undefined") return

    // Some embedded webviews and DOM test environments do not implement
    // matchMedia. Keep the same breakpoint contract reactive through resize
    // instead of crashing the entire shell.
    if (typeof window.matchMedia !== "function") {
      const syncFromWidth = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
      syncFromWidth()
      window.addEventListener("resize", syncFromWidth)
      return () => window.removeEventListener("resize", syncFromWidth)
    }

    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 0.02}px)`)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    setIsMobile(mq.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  // Keyboard detection via visualViewport (iOS Safari + Chrome)
  useEffect(() => {
    if (!isMobile || !window.visualViewport) return

    const vv = window.visualViewport
    const onResize = () => {
      const kbHeight = Math.max(0, window.innerHeight - vv.height)
      setKeyboardHeight(prev => prev === kbHeight ? prev : kbHeight)
    }
    vv.addEventListener("resize", onResize)
    onResize() // sync value immediately on attach
    return () => vv.removeEventListener("resize", onResize)
  }, [isMobile])

  return { isMobile, keyboardHeight, isKeyboardOpen: keyboardHeight > 100 }
}
